import { EmptyState, ErrorState, FreshnessBar } from "../components/AdminPageStates";
import { useFounderGrowth } from "../hooks/useFounderGrowth";
import { FounderGrowthMetric, FounderGrowthStory } from "../services/founderGrowthService";

const LOADING_CARDS = ["New Users Today", "New Coaches", "Bookings Today", "Revenue Today", "Retention", "Returning Users"];

function StoryCard({ story }: { story: FounderGrowthStory }) {
  return (
    <section className="founder-growth-story-card" aria-label={`${story.headline} ${story.insight}`}>
      <div className="founder-growth-story-topline">
        <span className="founder-growth-icon" aria-hidden="true">
          {story.icon}
        </span>
        <span className={`status-badge ${story.status}`}>{statusLabel(story.status)}</span>
      </div>
      <h2>{story.headline}</h2>
      <p>{story.insight}</p>
    </section>
  );
}

function MetricCard({ metric }: { metric: FounderGrowthMetric }) {
  return (
    <details className={`founder-growth-metric-card ${metric.status}`}>
      <summary>
        <span>
          <span className="founder-growth-metric-title">{metric.title}</span>
          <span className="founder-growth-metric-story">{metric.story}</span>
        </span>
        <strong>{metric.value}</strong>
      </summary>
      <div className="founder-growth-metric-detail">
        <p>{metric.detail}</p>
        <p>Source: {metric.source}</p>
        {metric.unavailableReason ? <p>{metric.unavailableReason}</p> : null}
      </div>
    </details>
  );
}

function LoadingCard({ title }: { title: string }) {
  return (
    <section className="founder-growth-metric-card loading-card" aria-busy="true">
      <p className="founder-growth-metric-title">{title}</p>
      <div className="skeleton skeleton-value" />
      <div className="skeleton skeleton-note" />
    </section>
  );
}

function statusLabel(status: FounderGrowthStory["status"]) {
  switch (status) {
    case "growing":
      return "Growing";
    case "healthy":
      return "Healthy";
    case "watching":
      return "Watching";
    case "future":
      return "Future";
    case "unavailable":
      return "Unavailable";
  }
}

export default function FounderGrowth() {
  const { data, error, isLoading, lastUpdated, refresh } = useFounderGrowth();

  if (error) {
    return <ErrorState message="Unable to load Founder Growth. Check Firebase permissions and aggregate access." />;
  }

  if (!isLoading && !data) {
    return <EmptyState message="Growth signals will appear as Chor activity is recorded." />;
  }

  return (
    <section className="founder-growth-page" aria-label="Founder Growth">
      <FreshnessBar
        dataSource="Read-only growth queries from users, bookings, challenges, classSessions, opportunities, marketplace_transactions, and adminAnalytics/retention"
        isLive={false}
        lastUpdated={lastUpdated}
        onRefresh={() => {
          void refresh();
        }}
      />

      <header className="founder-growth-hero">
        <p className="eyebrow">Founder growth</p>
        <h2>Is Chor growing?</h2>
        <p>
          Growth signals are presented as stories first, with source details available when you need to inspect the
          canonical truth behind each number.
        </p>
      </header>

      {isLoading || !data ? (
        <div className="founder-growth-metric-grid">
          {LOADING_CARDS.map((title) => (
            <LoadingCard key={title} title={title} />
          ))}
        </div>
      ) : (
        <>
          <StoryCard story={data.hero} />

          <div className="founder-growth-story-grid" aria-label="Meaningful growth changes">
            {data.stories.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
          </div>

          <section className="founder-growth-section" aria-labelledby="growth-signals-heading">
            <div className="founder-growth-section-header">
              <p className="eyebrow">Growth signals</p>
              <h3 id="growth-signals-heading">What changed today?</h3>
            </div>
            <div className="founder-growth-metric-grid">
              {data.metrics.map((metric) => (
                <MetricCard key={metric.id} metric={metric} />
              ))}
            </div>
          </section>
        </>
      )}
    </section>
  );
}
